import { useTranslation } from 'react-i18next';
import { Controller, useFormContext } from 'react-hook-form';
import { ColumnDef } from '@tanstack/react-table';
import { useUserRoles } from '@anytag/auth';
import { useTableInputCursor } from '@anytag/shared-hooks';
import { formatNumber, formatPercent } from '@anytag/shared-utils';
import { Input, TableCell, TextCutter } from '@anytag/shared-atoms';
import { YoutubeCMSAsset, YoutubeCMSRevenueType } from '../../types';
import { YoutubeCMSAssetOwnerChannelIdFormValues } from '../types';

interface Props {
  modifiable?: boolean;
  type: YoutubeCMSRevenueType;
  onSetDeletingAssetOwner: (owner: YoutubeCMSAsset | null) => void;
}

export const useYoutubeCMSAssetsTable = ({ type, modifiable = true, onSetDeletingAssetOwner }: Props) => {
  const { t } = useTranslation();
  const { isAdminStaff } = useUserRoles();
  const { control } = useFormContext<YoutubeCMSAssetOwnerChannelIdFormValues>();
  const { handleInputFocus, handleInputBlur } = useTableInputCursor<YoutubeCMSAssetOwnerChannelIdFormValues>();

  const isConfirmedRevenue = type === YoutubeCMSRevenueType.CONFIRMED_REVENUE;
  const canEditChannelId = !isConfirmedRevenue && modifiable;
  const columns: ColumnDef<YoutubeCMSAsset>[] = [
    {
      accessorKey: 'assetName',
      meta: { sticky: true },
      header: t<string>('HeaderColumn.Asset name'),
      cell: ({ row }) => (
        <div className="flex min-w-50">
          <div className="leading-[100%]">
            <TextCutter className="text-base" text={row.original.assetName || '-'} lines={2} />
            <p className="mt-1 text-xxs text-tertiary">
              {t('id')}: {row.original.assetId}
            </p>
          </div>
        </div>
      )
    },
    {
      accessorKey: 'rpm',
      header: t<string>('HeaderColumn.Estimated RPM'),
      meta: { align: 'right', hidden: !isAdminStaff || isConfirmedRevenue },
      cell: ({ row }) => <TableCell.Number value={formatNumber(row.original.rpm)} unit={t(row.original.currency)} />
    },
    {
      accessorKey: 'taRpm',
      header: t<string>('HeaderColumn.Estimated talent agency RPM'),
      meta: { align: 'right', hidden: isConfirmedRevenue },
      cell: ({ row }) => <TableCell.Number value={formatNumber(row.original.taRpm)} unit={t(row.original.currency)} />
    },
    {
      accessorKey: 'partnerRevenue',
      meta: { align: 'right', hidden: !isAdminStaff },
      header: t<string>(
        isConfirmedRevenue ? 'HeaderColumn.Confirmed partner revenue' : 'HeaderColumn.Estimated partner revenue'
      ),
      cell: ({ row }) => (
        <TableCell.Number value={formatNumber(row.original.partnerRevenue)} unit={t(row.original.currency)} />
      )
    },
    {
      accessorKey: 'taRevenue',
      meta: { align: 'right', hidden: isConfirmedRevenue },
      header: t<string>('HeaderColumn.Estimated talent agency revenue'),
      cell: ({ row }) => (
        <TableCell.Number value={formatNumber(row.original.taRevenue)} unit={t(row.original.currency)} />
      )
    },
    {
      accessorKey: 'shareCreator',
      header: t<string>(
        isConfirmedRevenue ? 'HeaderColumn.Confirmed share to creator' : 'HeaderColumn.Estimated share to creator'
      ),
      meta: { align: 'right' },
      cell: ({ row }) => (
        <TableCell.Number value={formatNumber(row.original.shareCreator)} unit={t(row.original.currency)} />
      )
    },
    {
      accessorKey: 'profit',
      meta: { align: 'right', hidden: !isAdminStaff },
      header: t<string>(isConfirmedRevenue ? 'HeaderColumn.Confirmed gross profit' : 'HeaderColumn.Estimated GP'),
      cell: ({ row }) => (
        <TableCell.Number value={formatNumber(row.original.profit || null)} unit={t(row.original.currency)} />
      )
    },
    {
      accessorKey: 'taProfit',
      meta: { align: 'right', hidden: isConfirmedRevenue },
      header: t<string>('HeaderColumn.Estimated talent agency GP'),
      cell: ({ row }) => (
        <TableCell.Number value={formatNumber(row.original.taProfit || null)} unit={t(row.original.currency)} />
      )
    },
    {
      accessorKey: 'taShareRate',
      header: t<string>('HeaderColumn.Talent agency margin rate'),
      meta: { align: 'right', hidden: !isAdminStaff || isConfirmedRevenue },
      cell: ({ row }) => <TableCell.Number value={formatPercent(row.original.taShareRate || null)} unit="%" />
    },
    {
      accessorKey: 'ownerChannelId',
      header: t<string>('HeaderColumn.Asset owner channel id'),
      cell: ({ row }) => {
        const { id, isOwner, ownerChannelId } = row.original;

        return (
          <div className="min-w-42.5">
            {!canEditChannelId || !id || ownerChannelId ? (
              <TextCutter
                text={ownerChannelId}
                className="box-border flex! h-8 w-64.5 items-center rounded-sm border border-black-40 bg-light-gray px-2 py-0 select-all"
              />
            ) : (
              <Controller<YoutubeCMSAssetOwnerChannelIdFormValues>
                control={control}
                name={`${id!}-ownerChannelId` satisfies keyof YoutubeCMSAssetOwnerChannelIdFormValues}
                render={({ field, fieldState: { error } }) => (
                  <Input
                    {...field}
                    className="w-64.5"
                    invalid={!!error}
                    onBlur={handleInputBlur}
                    onFocus={handleInputFocus}
                    disabled={!!ownerChannelId}
                    placeholder={t<string>('TextForm.Input channel id')}
                  />
                )}
              />
            )}

            {isOwner && canEditChannelId && id && ownerChannelId ? (
              <p
                onClick={() => onSetDeletingAssetOwner(row.original)}
                className="mt-1 cursor-pointer text-sm text-active"
              >
                {t('Clear owner')}
              </p>
            ) : null}
          </div>
        );
      }
    },
    {
      accessorKey: 'ownerChannelName',
      header: t<string>('HeaderColumn.Asset owner channel name'),
      cell: ({ row }) => <TableCell.Text value={row.original.ownerChannelName} lines={2} />
    }
  ];

  return { columns };
};
